import {
  type StaffCatalogCategoriesBody,
  type StaffCatalogCreateItemInput,
  type StaffCatalogDetailsPatch,
  type StaffCatalogItemDetail,
  type StaffCatalogItemRecord,
  type StaffCatalogItemType,
  type StaffCatalogMediaBody,
  type StaffCatalogPeopleBody,
  type StaffCatalogPricingInput,
  type StaffCatalogSubtypeInput,
  type StaffCatalogSubtypeRecord,
  type StaffCatalogTagsBody,
  staffCatalogCreateItemBodySchema,
  staffCatalogDetailsPatchSchema,
  staffCatalogMediaBodySchema,
  staffCatalogPricingSchema,
  staffCatalogSubtypeInputSchema,
} from "@lafayette/schemas";

import { CURRENCY_PATTERN, minorToMajor, parseMajorToMinor } from "./money";
import {
  instantToUtcLocal,
  instantToZonedLocal,
  isValidTimeZone,
  utcLocalToInstant,
  zonedLocalToInstant,
} from "./time";
import { describeIssue, type FieldErrors, issuesToFieldErrors } from "./validation";

// The translation between what the API sends (a detail), what a form holds (strings), and what the API
// accepts (a request body). Every function is pure. A form value is always a string: an input never
// holds a number or a null, and "" means "empty". The request schemas of `@lafayette/schemas` validate
// the body, so the form refuses what the server would refuse; an issue is attached to a form field.

export type MapResult<T> =
  | Readonly<{ ok: true; value: T }>
  | Readonly<{ ok: false; errors: FieldErrors }>;

const fail = (errors: FieldErrors): { ok: false; errors: FieldErrors } => ({ ok: false, errors });

// --- small parsers -----------------------------------------------------------------------------------

function textOrNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

/** "" is `null`; a whole number is a number; anything else is `undefined` (an error for the field). */
function wholeOrNull(value: string): number | null | undefined {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  return /^-?\d{1,9}$/.test(trimmed) ? Number(trimmed) : undefined;
}

function decimalOrNull(value: string): number | null | undefined {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  if (!/^-?\d{1,3}(\.\d+)?$/.test(trimmed)) return undefined;
  return Number(trimmed);
}

const str = (value: string | null | undefined): string => value ?? "";
const numStr = (value: number | null | undefined): string =>
  value === null || value === undefined ? "" : String(value);

// --- common fields (W1 and W2) ------------------------------------------------------------------------

const commonKeys = [
  "name",
  "slug",
  "shortDescription",
  "description",
  "addressText",
  "locality",
  "region",
  "countryCode",
  "latitude",
  "longitude",
  "timeZone",
  "fulfilmentMode",
  "externalUrl",
  "editorialPriority",
] as const;
export type CommonKey = (typeof commonKeys)[number];
export type CommonValues = Readonly<Record<CommonKey, string>>;

export function emptyCommonValues(): CommonValues {
  return {
    name: "",
    slug: "",
    shortDescription: "",
    description: "",
    addressText: "",
    locality: "",
    region: "",
    countryCode: "",
    latitude: "",
    longitude: "",
    timeZone: "",
    fulfilmentMode: "",
    externalUrl: "",
    editorialPriority: "0",
  };
}

export function commonValuesFromItem(item: StaffCatalogItemRecord): CommonValues {
  return {
    name: item.name,
    slug: item.slug,
    shortDescription: str(item.shortDescription),
    description: str(item.description),
    addressText: str(item.addressText),
    locality: str(item.locality),
    region: str(item.region),
    countryCode: str(item.countryCode),
    latitude: numStr(item.latitude),
    longitude: numStr(item.longitude),
    timeZone: str(item.timeZone),
    fulfilmentMode: item.fulfilmentMode,
    externalUrl: str(item.externalUrl),
    editorialPriority: String(item.editorialPriority),
  };
}

type CommonParsed = Partial<Record<CommonKey, string | number | null>>;

/** Every common field as the request carries it. A number field that is not a number is an error. */
function parseCommon(values: CommonValues): {
  parsed: CommonParsed;
  errors: Record<string, string>;
} {
  const errors: Record<string, string> = {};
  const latitude = decimalOrNull(values.latitude);
  const longitude = decimalOrNull(values.longitude);
  const priority = wholeOrNull(values.editorialPriority);
  if (latitude === undefined) errors.latitude = "Enter a number, for example 36.7213.";
  if (longitude === undefined) errors.longitude = "Enter a number, for example -4.4214.";
  if (priority === undefined || priority === null)
    errors.editorialPriority = "Enter a whole number.";
  const parsed: CommonParsed = {
    name: values.name.trim(),
    slug: values.slug.trim(),
    shortDescription: textOrNull(values.shortDescription),
    description: textOrNull(values.description),
    addressText: textOrNull(values.addressText),
    locality: textOrNull(values.locality),
    region: textOrNull(values.region),
    countryCode: textOrNull(values.countryCode)?.toUpperCase() ?? null,
    latitude: latitude ?? null,
    longitude: longitude ?? null,
    timeZone: textOrNull(values.timeZone),
    fulfilmentMode: values.fulfilmentMode,
    externalUrl: textOrNull(values.externalUrl),
    editorialPriority: priority ?? 0,
  };
  return { parsed, errors };
}

const itemValueOf = (item: StaffCatalogItemRecord, key: CommonKey): string | number | null =>
  item[key];

/** W2: only the fields that changed. A cleared optional field is sent as `null`. */
export function commonValuesToPatch(
  values: CommonValues,
  item: StaffCatalogItemRecord,
): MapResult<StaffCatalogDetailsPatch> {
  const { parsed, errors } = parseCommon(values);
  const patch: Record<string, string | number | null> = {};
  for (const key of commonKeys) {
    const next = parsed[key];
    if (next !== undefined && next !== itemValueOf(item, key)) patch[key] = next;
  }
  const checked = staffCatalogDetailsPatchSchema.safeParse(patch);
  if (!checked.success) {
    return fail({
      ...issuesToFieldErrors(checked.error.issues, (path) =>
        typeof path[0] === "string" ? path[0] : null,
      ),
      ...errors,
    });
  }
  if (Object.keys(errors).length > 0) return fail(errors);
  return { ok: true, value: patch as StaffCatalogDetailsPatch };
}

// --- subtype fields (W3, and part of W1) ----------------------------------------------------------------

export type SubtypeValues = Readonly<Record<string, string>>;

type Spec = Readonly<{ key: string; kind: "text" | "whole" | "enum" | "time" }>;

const text = (key: string): Spec => ({ key, kind: "text" });
const whole = (key: string): Spec => ({ key, kind: "whole" });
const pick = (key: string): Spec => ({ key, kind: "enum" });
const time = (key: string): Spec => ({ key, kind: "time" });

/** The plain fields of each type. The fields that need a conversion (event times, dining spend) are below. */
const subtypeSpecs: Readonly<Record<StaffCatalogItemType, readonly Spec[]>> = {
  DINING: [pick("dressCode"), pick("reservationPolicy"), text("reservationNotes")],
  VILLA: [
    whole("bedrooms"),
    whole("bathrooms"),
    whole("maxGuests"),
    whole("minimumStayNights"),
    time("checkInTime"),
    time("checkOutTime"),
  ],
  EVENT: [
    text("venueName"),
    pick("schedulePrecision"),
    pick("eventStatus"),
    text("ticketingNotes"),
  ],
  EXPERIENCE: [
    whole("durationMinutes"),
    pick("privacy"),
    whole("groupMin"),
    whole("groupMax"),
    whole("minimumAge"),
    text("eligibilityNotes"),
    text("includes"),
    text("excludes"),
    text("meetingPointText"),
  ],
  SERVICE: [text("serviceAreaText"), whole("leadTimeHours"), text("availabilityNotes")],
};

export function emptySubtypeValues(type: StaffCatalogItemType): SubtypeValues {
  const values: Record<string, string> = {};
  for (const spec of subtypeSpecs[type]) values[spec.key] = "";
  if (type === "DINING") {
    values.typicalSpendAmount = "";
    values.typicalSpendCurrency = "";
  }
  if (type === "EVENT") {
    values.timeZone = "";
    values.startsAt = "";
    values.endsAt = "";
    values.schedulePrecision = "EXACT";
    values.eventStatus = "SCHEDULED";
  }
  return values;
}

export function subtypeValuesFromRecord(record: StaffCatalogSubtypeRecord): SubtypeValues {
  const values: Record<string, string> = { ...emptySubtypeValues(record.type) };
  const source = record as Record<string, unknown>;
  for (const spec of subtypeSpecs[record.type]) {
    const value = source[spec.key];
    values[spec.key] = value === null || value === undefined ? "" : String(value);
  }
  if (record.type === "DINING" && record.typicalSpendPerPerson !== null) {
    values.typicalSpendCurrency = record.typicalSpendPerPerson.currency;
    values.typicalSpendAmount = minorToMajor(
      record.typicalSpendPerPerson.amountMinor,
      record.typicalSpendPerPerson.currency,
    );
  }
  if (record.type === "EVENT") {
    values.timeZone = record.timeZone;
    values.startsAt = instantToZonedLocal(record.startsAt, record.timeZone) ?? "";
    values.endsAt =
      record.endsAt === null ? "" : (instantToZonedLocal(record.endsAt, record.timeZone) ?? "");
  }
  return values;
}

/** Builds the raw request object for a type, collecting the field errors the conversions find. */
function buildSubtype(
  type: StaffCatalogItemType,
  values: SubtypeValues,
  errors: Record<string, string>,
): Record<string, unknown> {
  const body: Record<string, unknown> = { type };
  for (const spec of subtypeSpecs[type]) {
    const raw = values[spec.key] ?? "";
    if (spec.kind === "whole") {
      const parsed = wholeOrNull(raw);
      if (parsed === undefined) errors[spec.key] = "Enter a whole number.";
      else body[spec.key] = parsed;
    } else {
      body[spec.key] = textOrNull(raw);
    }
  }
  if (type === "DINING") {
    const amount = (values.typicalSpendAmount ?? "").trim();
    const currency = (values.typicalSpendCurrency ?? "").trim().toUpperCase();
    if (amount === "" && currency === "") {
      body.typicalSpendPerPerson = null;
    } else if (!CURRENCY_PATTERN.test(currency)) {
      errors.typicalSpendCurrency = "Enter a three-letter currency code, for example EUR.";
    } else {
      const minor = parseMajorToMinor(amount, currency);
      if (minor === null) errors.typicalSpendAmount = "Enter an amount, for example 45.50.";
      else body.typicalSpendPerPerson = { amountMinor: minor, currency };
    }
  }
  if (type === "EVENT") {
    const timeZone = (values.timeZone ?? "").trim();
    body.timeZone = timeZone;
    const zoneOk = isValidTimeZone(timeZone);
    if (timeZone !== "" && !zoneOk)
      errors.timeZone = "Enter a valid time zone, for example Europe/Madrid.";
    const startsAt = (values.startsAt ?? "").trim();
    const endsAt = (values.endsAt ?? "").trim();
    if (startsAt === "") {
      errors.startsAt = "Enter the start date and time.";
    } else if (zoneOk) {
      const instant = zonedLocalToInstant(startsAt, timeZone);
      if (instant === null) errors.startsAt = "Enter a valid date and time.";
      else body.startsAt = instant.toISOString();
    }
    if (endsAt === "") {
      body.endsAt = null;
    } else if (zoneOk) {
      const instant = zonedLocalToInstant(endsAt, timeZone);
      if (instant === null) errors.endsAt = "Enter a valid date and time.";
      else body.endsAt = instant.toISOString();
    }
  }
  return body;
}

/** The form field that an issue path belongs to. The event's instants have their own form fields. */
function subtypeFieldOf(path: readonly PropertyKey[]): string | null {
  const key = path[0];
  if (typeof key !== "string") return null;
  return key === "typicalSpendPerPerson"
    ? path[1] === "currency"
      ? "typicalSpendCurrency"
      : "typicalSpendAmount"
    : key;
}

export function subtypeValuesToInput(
  type: StaffCatalogItemType,
  values: SubtypeValues,
): MapResult<StaffCatalogSubtypeInput> {
  const errors: Record<string, string> = {};
  const body = buildSubtype(type, values, errors);
  const checked = staffCatalogSubtypeInputSchema.safeParse(body);
  if (!checked.success) {
    const fromSchema = issuesToFieldErrors(checked.error.issues, subtypeFieldOf);
    return fail({ ...fromSchema, ...errors });
  }
  if (Object.keys(errors).length > 0) return fail(errors);
  return { ok: true, value: body as StaffCatalogSubtypeInput };
}

// --- create (W1) ---------------------------------------------------------------------------------------

export type CreateErrors = Readonly<{ common: FieldErrors; subtype: FieldErrors }>;

/** The body of W1. A blank optional field is left out (the server decides its default). */
export function createValuesToInput(
  type: StaffCatalogItemType,
  common: CommonValues,
  subtype: SubtypeValues,
):
  | Readonly<{ ok: true; value: StaffCatalogCreateItemInput }>
  | Readonly<{ ok: false; errors: CreateErrors }> {
  const { parsed, errors: commonErrors } = parseCommon(common);
  const subtypeErrors: Record<string, string> = {};
  const subtypeBody = buildSubtype(type, subtype, subtypeErrors);

  const body: Record<string, unknown> = { itemType: type, subtype: subtypeBody };
  for (const key of commonKeys) {
    const value = parsed[key];
    if (value === null || value === undefined) continue;
    if (key === "fulfilmentMode" && value === "") continue;
    body[key] = value;
  }
  if (common.editorialPriority.trim() === "" || common.editorialPriority.trim() === "0") {
    delete body.editorialPriority;
  }

  const checked = staffCatalogCreateItemBodySchema.safeParse(body);
  const schemaCommon: Record<string, string> = {};
  const schemaSubtype: Record<string, string> = {};
  if (!checked.success) {
    for (const issue of checked.error.issues) {
      const [head, ...rest] = issue.path;
      if (head === "subtype") {
        const field = subtypeFieldOf(rest) ?? "";
        schemaSubtype[field] ??= describeIssue(issue);
      } else if (typeof head === "string") {
        schemaCommon[head] ??= describeIssue(issue);
      }
    }
  }
  const errors: CreateErrors = {
    common: { ...schemaCommon, ...commonErrors },
    subtype: { ...schemaSubtype, ...subtypeErrors },
  };
  if (Object.keys(errors.common).length > 0 || Object.keys(errors.subtype).length > 0) {
    return { ok: false, errors };
  }
  return { ok: true, value: body as StaffCatalogCreateItemInput };
}

// --- pricing (W4) ----------------------------------------------------------------------------------------

const pricingKeys = [
  "model",
  "currency",
  "amount",
  "minAmount",
  "maxAmount",
  "unit",
  "terms",
  "source",
  "validFrom",
  "validUntil",
  "noExpiry",
] as const;
export type PricingKey = (typeof pricingKeys)[number];
export type PricingValues = Readonly<Record<PricingKey, string>>;

/** The models that carry an amount, and which amount fields each uses. */
export function pricingAmountFields(
  model: string,
): readonly ("amount" | "minAmount" | "maxAmount")[] {
  if (model === "FIXED" || model === "FROM") return ["amount"];
  if (model === "RANGE") return ["minAmount", "maxAmount"];
  return [];
}

export function pricingValuesFromItem(item: StaffCatalogItemRecord): PricingValues {
  const currency = item.priceCurrency ?? "";
  const major = (minor: number | null) =>
    minor === null ? "" : currency === "" ? String(minor) : minorToMajor(minor, currency);
  return {
    model: item.pricingModel,
    currency,
    amount: major(item.priceAmountMinor),
    minAmount: major(item.priceMinAmountMinor),
    maxAmount: major(item.priceMaxAmountMinor),
    unit: item.priceUnit ?? "",
    terms: str(item.priceTerms),
    source: item.priceSource ?? "",
    validFrom: item.priceValidFrom === null ? "" : instantToUtcLocal(item.priceValidFrom),
    validUntil: item.priceValidUntil === null ? "" : instantToUtcLocal(item.priceValidUntil),
    noExpiry: item.priceNoExpiry ? "true" : "false",
  };
}

const PRICING_FIELD_OF: Readonly<Record<string, PricingKey>> = {
  model: "model",
  currency: "currency",
  amountMinor: "amount",
  minAmountMinor: "minAmount",
  maxAmountMinor: "maxAmount",
  unit: "unit",
  terms: "terms",
  source: "source",
  validFrom: "validFrom",
  validUntil: "validUntil",
  noExpiry: "noExpiry",
};

export function pricingValuesToInput(values: PricingValues): MapResult<StaffCatalogPricingInput> {
  const errors: Record<string, string> = {};
  const amountFields = pricingAmountFields(values.model);
  const body: Record<string, unknown> = { model: values.model };
  const terms = textOrNull(values.terms);
  if (terms !== null) body.terms = terms;

  if (amountFields.length > 0) {
    const currency = values.currency.trim().toUpperCase();
    if (!CURRENCY_PATTERN.test(currency)) {
      errors.currency = "Enter a three-letter currency code, for example EUR.";
    } else {
      body.currency = currency;
    }
    const names = {
      amount: "amountMinor",
      minAmount: "minAmountMinor",
      maxAmount: "maxAmountMinor",
    } as const;
    for (const field of amountFields) {
      const raw = values[field].trim();
      if (raw === "") {
        errors[field] = "Enter an amount.";
      } else if (CURRENCY_PATTERN.test(currency)) {
        const minor = parseMajorToMinor(raw, currency);
        if (minor === null) errors[field] = "Enter an amount, for example 120.50.";
        else body[names[field]] = minor;
      }
    }
    const unit = textOrNull(values.unit);
    if (unit !== null) body.unit = unit;
    const source = textOrNull(values.source);
    if (source !== null) body.source = source;
    for (const field of ["validFrom", "validUntil"] as const) {
      const raw = values[field].trim();
      if (raw === "") continue;
      const instant = utcLocalToInstant(raw);
      if (instant === null) errors[field] = "Enter a valid date and time.";
      else body[field] = instant.toISOString();
    }
    if (values.noExpiry === "true") body.noExpiry = true;
  }

  const checked = staffCatalogPricingSchema.safeParse(body);
  if (!checked.success) {
    const fromSchema = issuesToFieldErrors(checked.error.issues, (path) =>
      typeof path[0] === "string" ? (PRICING_FIELD_OF[path[0]] ?? null) : null,
    );
    return fail({ ...fromSchema, ...errors });
  }
  if (Object.keys(errors).length > 0) return fail(errors);
  return { ok: true, value: body as StaffCatalogPricingInput };
}

// --- links: categories, tags, people (W6 to W8) --------------------------------------------------------------

export type CategoryDraft = Readonly<{ code: string; name: string; isPrimary: boolean }>;
export type TagDraft = Readonly<{ namespace: string; code: string; label: string }>;
export type PersonDraft = Readonly<{ slug: string; displayName: string; role: string }>;

export const categoryDraftsFromDetail = (
  detail: StaffCatalogItemDetail,
): readonly CategoryDraft[] =>
  detail.categories.map((link) => ({
    code: link.code,
    name: link.name,
    isPrimary: link.isPrimary,
  }));

export const tagDraftsFromDetail = (detail: StaffCatalogItemDetail): readonly TagDraft[] =>
  detail.tags.map((link) => ({ namespace: link.namespace, code: link.code, label: link.label }));

/** The order of the API's list is the order on the page (position is the index). */
export const personDraftsFromDetail = (detail: StaffCatalogItemDetail): readonly PersonDraft[] =>
  [...detail.people]
    .sort((a, b) => a.position - b.position)
    .map((link) => ({ slug: link.slug, displayName: link.displayName, role: link.role }));

export function categoriesToBody(
  drafts: readonly CategoryDraft[],
): StaffCatalogCategoriesBody["categories"] {
  return drafts.map((draft) => ({ code: draft.code, isPrimary: draft.isPrimary }));
}

export function tagsToBody(drafts: readonly TagDraft[]): StaffCatalogTagsBody["tags"] {
  return drafts.map((draft) => ({
    namespace: draft.namespace as StaffCatalogTagsBody["tags"][number]["namespace"],
    code: draft.code,
  }));
}

export function peopleToBody(drafts: readonly PersonDraft[]): StaffCatalogPeopleBody["people"] {
  return drafts.map((draft) => ({
    slug: draft.slug,
    role: draft.role as StaffCatalogPeopleBody["people"][number]["role"],
  }));
}

/** Adds a category. The first category of a list is the primary one. A code already in the list is kept once. */
export function addCategory(
  list: readonly CategoryDraft[],
  entry: Omit<CategoryDraft, "isPrimary">,
): readonly CategoryDraft[] {
  if (list.some((existing) => existing.code === entry.code)) return list;
  return [...list, { ...entry, isPrimary: list.length === 0 }];
}

/** Removes a category. When the primary one goes, the first one left becomes primary. */
export function removeCategory(
  list: readonly CategoryDraft[],
  code: string,
): readonly CategoryDraft[] {
  const rest = list.filter((entry) => entry.code !== code);
  if (rest.length === 0 || rest.some((entry) => entry.isPrimary)) return rest;
  return rest.map((entry, index) => ({ ...entry, isPrimary: index === 0 }));
}

export function setPrimaryCategory(
  list: readonly CategoryDraft[],
  code: string,
): readonly CategoryDraft[] {
  return list.map((entry) => ({ ...entry, isPrimary: entry.code === code }));
}

export function addTag(list: readonly TagDraft[], entry: TagDraft): readonly TagDraft[] {
  return list.some(
    (existing) => existing.namespace === entry.namespace && existing.code === entry.code,
  )
    ? list
    : [...list, entry];
}

export function removeTag(
  list: readonly TagDraft[],
  entry: Pick<TagDraft, "namespace" | "code">,
): readonly TagDraft[] {
  return list.filter(
    (existing) => !(existing.namespace === entry.namespace && existing.code === entry.code),
  );
}

/** A person can hold several roles on one item, so the pair (slug, role) is the key. */
export function addPerson(
  list: readonly PersonDraft[],
  entry: PersonDraft,
): readonly PersonDraft[] {
  return list.some((existing) => existing.slug === entry.slug && existing.role === entry.role)
    ? list
    : [...list, entry];
}

/** Moves the entry at `index` by `delta` places. Out of range leaves the list as it is. */
export function moveItem<T>(list: readonly T[], index: number, delta: number): readonly T[] {
  const target = index + delta;
  if (index < 0 || index >= list.length || target < 0 || target >= list.length) return list;
  const next = [...list];
  const [moved] = next.splice(index, 1);
  next.splice(target, 0, moved as T);
  return next;
}

// --- media (W9) ------------------------------------------------------------------------------------------------

/** One media row of the form. The preview URL is not part of the draft: it changes on every read. */
export type MediaDraft = Readonly<{
  storageObjectPath: string;
  kind: "IMAGE" | "VIDEO";
  altText: string;
  caption: string;
  credit: string;
  isCover: boolean;
}>;

/** In page order (the API's `position`). */
export function mediaDraftsFromDetail(detail: StaffCatalogItemDetail): readonly MediaDraft[] {
  return [...detail.media]
    .sort((a, b) => a.record.position - b.record.position)
    .map(({ record }) => ({
      storageObjectPath: record.storageObjectPath,
      kind: record.kind,
      altText: str(record.altText),
      caption: str(record.caption),
      credit: str(record.credit),
      isCover: record.isCover,
    }));
}

/** A new upload joins the list. The first image of an item is its cover. */
export function appendMedia(
  list: readonly MediaDraft[],
  storageObjectPath: string,
): readonly MediaDraft[] {
  if (list.some((entry) => entry.storageObjectPath === storageObjectPath)) return list;
  return [
    ...list,
    {
      storageObjectPath,
      kind: "IMAGE",
      altText: "",
      caption: "",
      credit: "",
      isCover: !list.some((entry) => entry.isCover),
    },
  ];
}

export function setCoverMedia(
  list: readonly MediaDraft[],
  storageObjectPath: string,
): readonly MediaDraft[] {
  return list.map((entry) => ({
    ...entry,
    isCover: entry.storageObjectPath === storageObjectPath,
  }));
}

/** Removes a row. When the cover goes, the first row left becomes the cover. */
export function removeMedia(
  list: readonly MediaDraft[],
  storageObjectPath: string,
): readonly MediaDraft[] {
  const rest = list.filter((entry) => entry.storageObjectPath !== storageObjectPath);
  if (rest.length === 0 || rest.some((entry) => entry.isCover)) return rest;
  return rest.map((entry, index) => ({ ...entry, isCover: index === 0 }));
}

export function updateMedia(
  list: readonly MediaDraft[],
  storageObjectPath: string,
  change: Partial<Pick<MediaDraft, "altText" | "caption" | "credit">>,
): readonly MediaDraft[] {
  return list.map((entry) =>
    entry.storageObjectPath === storageObjectPath ? { ...entry, ...change } : entry,
  );
}

export function mediaToBody(drafts: readonly MediaDraft[]): StaffCatalogMediaBody["media"] {
  return drafts.map((draft) => ({
    storageObjectPath: draft.storageObjectPath,
    kind: draft.kind,
    altText: textOrNull(draft.altText),
    caption: textOrNull(draft.caption),
    credit: textOrNull(draft.credit),
    isCover: draft.isCover,
  }));
}

export const MEDIA_ERROR_SEPARATOR = "|";

/** Errors keyed `<storageObjectPath>|<field>`: a row keeps its error while the list is reordered. */
export function validateMedia(drafts: readonly MediaDraft[]): FieldErrors {
  const media = mediaToBody(drafts);
  const checked = staffCatalogMediaBodySchema.safeParse({ expectedVersion: 1, media });
  if (checked.success) return {};
  const errors: Record<string, string> = {};
  for (const issue of checked.error.issues) {
    const [head, index, field] = issue.path;
    const row = typeof index === "number" ? drafts[index] : undefined;
    if (head !== "media" || row === undefined || typeof field !== "string") {
      errors[""] ??= "The media list is not valid.";
      continue;
    }
    const key = `${row.storageObjectPath}${MEDIA_ERROR_SEPARATOR}${field}`;
    errors[key] ??= describeIssue(issue);
  }
  return errors;
}
